import { ChangeDetectionStrategy, Component, output, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="sticky top-0 z-50 bg-[#0A0A0B]/95 backdrop-blur border-b border-white/10 text-white shadow-xl shadow-black/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <!-- Logo / Title -->
          <button 
            type="button"
            (click)="onSelect('hero')" 
            class="flex items-center space-x-3 text-left focus:outline-none cursor-pointer group">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F27D26] to-[#d86b1c] flex items-center justify-center text-black font-bold shadow-lg shadow-[#F27D26]/20 group-hover:scale-105 transition-transform">
              <span class="material-symbols-outlined text-2xl">router</span>
            </div>
            <div>
              <div class="flex items-center space-x-2">
                <h1 class="text-lg font-bold tracking-tight text-white leading-tight">Empreendedorismo em <span class="text-[#F27D26] font-serif italic">Redes</span></h1>
                <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-medium bg-[#F27D26]/10 text-[#F27D26] border border-[#F27D26]/30">
                  <span class="w-1.5 h-1.5 rounded-full bg-[#F27D26] animate-ping mr-1.5"></span>
                  40 Horas
                </span>
              </div>
            </div>
          </button>

          <!-- Quick Navigation Tabs (Desktop) -->
          <nav class="hidden md:flex items-center space-x-1 lg:space-x-2">
            @for (nav of navItems; track nav.id) {
              <button 
                type="button"
                (click)="onSelect(nav.id)"
                [class]="activeTab() === nav.id 
                  ? 'bg-[#F27D26] text-black font-bold border-[#F27D26] shadow-sm shadow-[#F27D26]/30' 
                  : 'text-zinc-300 hover:bg-white/5 hover:text-white border-transparent'"
                class="px-3 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center space-x-1.5 cursor-pointer">
                <span class="material-symbols-outlined text-base">{{ nav.icon }}</span>
                <span>{{ nav.label }}</span>
              </button>
            }

            <!-- Menu Aulas (Dropdown) -->
            <div class="relative">
              <button 
                type="button"
                (click)="toggleAulasDropdown()"
                [class]="(activeTab() === 'aulas' || activeTab() === 'aula1' || activeTab() === 'aula2')
                  ? 'bg-[#F27D26] text-black font-bold border-[#F27D26] shadow-sm shadow-[#F27D26]/30' 
                  : 'text-zinc-300 hover:bg-white/5 hover:text-white border-white/10 bg-white/5'"
                class="px-3.5 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center space-x-1.5 cursor-pointer">
                <span class="material-symbols-outlined text-base">school</span>
                <span>Menu Aulas</span>
                <span class="material-symbols-outlined text-sm transition-transform duration-200" [class.rotate-180]="aulasDropdownOpen()">
                  keyboard_arrow_down
                </span>
              </button>

              <!-- Dropdown Menu -->
              @if (aulasDropdownOpen()) {
                <div 
                  (mouseleave)="aulasDropdownOpen.set(false)"
                  class="absolute right-0 mt-2 w-72 bg-[#121214] border border-white/15 rounded-xl shadow-2xl p-2 space-y-1 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  <div class="px-3 py-2 text-[10px] font-mono font-semibold uppercase text-zinc-400 border-b border-white/10 flex items-center justify-between">
                    <span>Aulas do Curso</span>
                    <span class="text-[#F27D26]">4 Aulas</span>
                  </div>

                  <!-- Option 1: Hub Geral de Aulas -->
                  <button
                    type="button"
                    (click)="onSelect('aulas')"
                    [class.bg-white/10]="activeTab() === 'aulas'"
                    class="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-[#F27D26]/10 text-white flex items-center justify-between group transition-colors">
                    <div class="flex items-center space-x-2">
                      <span class="material-symbols-outlined text-base text-[#F27D26]">dashboard</span>
                      <span class="font-bold">Hub de Aulas (Visão Geral)</span>
                    </div>
                    <span class="material-symbols-outlined text-xs opacity-0 group-hover:opacity-100 transition-opacity">arrow_forward</span>
                  </button>

                  <div class="border-t border-white/10 my-1"></div>

                  <!-- Aula 1 (Liberada) -->
                  <button
                    type="button"
                    (click)="onSelect('aula1')"
                    [class.bg-[#F27D26]/20]="activeTab() === 'aula1'"
                    class="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-[#F27D26]/15 text-white flex items-center justify-between group transition-colors">
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono flex items-center justify-center font-bold text-[10px]">
                        01
                      </div>
                      <div>
                        <div class="font-bold text-zinc-100 group-hover:text-[#F27D26] transition-colors">Aula 1: Dinâmica das Dores</div>
                        <div class="text-[10px] text-zinc-400">Atividade Prática em Grupo</div>
                      </div>
                    </div>
                    <span class="px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold">
                      Ativa
                    </span>
                  </button>

                  <!-- Aula 2 (Liberada) -->
                  <button
                    type="button"
                    (click)="onSelect('aula2')"
                    [class.bg-[#F27D26]/20]="activeTab() === 'aula2'"
                    class="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-[#F27D26]/15 text-white flex items-center justify-between group transition-colors">
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono flex items-center justify-center font-bold text-[10px]">
                        02
                      </div>
                      <div>
                        <div class="font-bold text-zinc-100 group-hover:text-[#F27D26] transition-colors">Aula 2: A Jornada do Empreendedor</div>
                        <div class="text-[10px] text-zinc-400">Slides & Desafio de Campo</div>
                      </div>
                    </div>
                    <span class="px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold">
                      Ativa
                    </span>
                  </button>

                  <!-- Aula 3 (Liberada) -->
                  <button
                    type="button"
                    (click)="onSelect('aula3')"
                    [class.bg-[#F27D26]/20]="activeTab() === 'aula3'"
                    class="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-[#F27D26]/15 text-white flex items-center justify-between group transition-colors">
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono flex items-center justify-center font-bold text-[10px]">
                        03
                      </div>
                      <div>
                        <div class="font-bold text-zinc-100 group-hover:text-[#F27D26] transition-colors">Aula 3: As 10 CCEs em Redes</div>
                        <div class="text-[10px] text-zinc-400">12 Slides & RPG Brisanet</div>
                      </div>
                    </div>
                    <span class="px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold">
                      Ativa
                    </span>
                  </button>

                  <!-- Aula 4 (Liberada) -->
                  <button
                    type="button"
                    (click)="onSelect('aula4')"
                    [class.bg-[#F27D26]/20]="activeTab() === 'aula4'"
                    class="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-[#F27D26]/15 text-white flex items-center justify-between group transition-colors">
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono flex items-center justify-center font-bold text-[10px]">
                        04
                      </div>
                      <div>
                        <div class="font-bold text-zinc-100 group-hover:text-[#F27D26] transition-colors">Aula 4: Tipos & Posicionamento</div>
                        <div class="text-[10px] text-zinc-400">11 Slides & Folha de Atividades</div>
                      </div>
                    </div>
                    <span class="px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold">
                      Ativa
                    </span>
                  </button>

                  <!-- Aula 5 (Liberada) -->
                  <button
                    type="button"
                    (click)="onSelect('aula5')"
                    [class.bg-[#F27D26]/20]="activeTab() === 'aula5'"
                    class="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-[#F27D26]/15 text-white flex items-center justify-between group transition-colors">
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono flex items-center justify-center font-bold text-[10px]">
                        05
                      </div>
                      <div>
                        <div class="font-bold text-zinc-100 group-hover:text-[#F27D26] transition-colors">Aula Especial: Fome de Poder</div>
                        <div class="text-[10px] text-zinc-400">10 Slides & Estudo de Caso</div>
                      </div>
                    </div>
                    <span class="px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold">
                      Ativa
                    </span>
                  </button>
                </div>
              }
            </div>
          </nav>

          <!-- Mobile Menu Button -->
          <div class="flex md:hidden">
            <button 
              type="button"
              (click)="toggleMobileMenu()" 
              class="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 focus:outline-none"
              aria-label="Abrir menu">
              <span class="material-symbols-outlined text-2xl">
                {{ mobileMenuOpen() ? 'close' : 'menu' }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile Dropdown Menu -->
      @if (mobileMenuOpen()) {
        <div class="md:hidden bg-[#0A0A0B] border-b border-white/10 px-4 pt-2 pb-4 space-y-1">
          @for (nav of navItems; track nav.id) {
            <button 
              type="button"
              (click)="onMobileSelect(nav.id)"
              [class]="activeTab() === nav.id ? 'bg-[#F27D26] text-black font-bold' : 'text-zinc-300 hover:bg-white/5'"
              class="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium flex items-center space-x-2">
              <span class="material-symbols-outlined text-lg">{{ nav.icon }}</span>
              <span>{{ nav.label }}</span>
            </button>
          }

          <!-- Mobile Aulas Hub & Aula Items -->
          <div class="pt-2 border-t border-white/10">
            <div class="px-3 py-1 text-[10px] font-mono text-zinc-400 uppercase font-semibold">
              Menu de Aulas (5 Aulas)
            </div>
            <button 
              type="button"
              (click)="onMobileSelect('aulas')"
              [class]="activeTab() === 'aulas' ? 'bg-[#F27D26] text-black font-bold' : 'text-white hover:bg-white/5'"
              class="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium flex items-center space-x-2">
              <span class="material-symbols-outlined text-lg text-[#F27D26]">school</span>
              <span>Hub de Aulas (Ver Todas)</span>
            </button>

            <button 
              type="button"
              (click)="onMobileSelect('aula1')"
              [class]="activeTab() === 'aula1' ? 'bg-[#F27D26] text-black font-bold' : 'text-zinc-300 hover:bg-white/5'"
              class="w-full text-left px-4 py-2 rounded-lg text-xs font-medium flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <span class="material-symbols-outlined text-base text-[#F27D26]">groups</span>
                <span>Aula 1: Dinâmica das Dores</span>
              </div>
              <span class="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-mono font-bold">ATIVA</span>
            </button>

            <button 
              type="button"
              (click)="onMobileSelect('aula2')"
              [class]="activeTab() === 'aula2' ? 'bg-[#F27D26] text-black font-bold' : 'text-zinc-300 hover:bg-white/5'"
              class="w-full text-left px-4 py-2 rounded-lg text-xs font-medium flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <span class="material-symbols-outlined text-base text-[#F27D26]">hub</span>
                <span>Aula 2: A Jornada do Empreendedor</span>
              </div>
              <span class="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-mono font-bold">ATIVA</span>
            </button>

            <button 
              type="button"
              (click)="onMobileSelect('aula3')"
              [class]="activeTab() === 'aula3' ? 'bg-[#F27D26] text-black font-bold' : 'text-zinc-300 hover:bg-white/5'"
              class="w-full text-left px-4 py-2 rounded-lg text-xs font-medium flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <span class="material-symbols-outlined text-base text-[#F27D26]">psychology</span>
                <span>Aula 3: As 10 CCEs em Redes</span>
              </div>
              <span class="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-mono font-bold">ATIVA</span>
            </button>

            <button 
              type="button"
              (click)="onMobileSelect('aula4')"
              [class]="activeTab() === 'aula4' ? 'bg-[#F27D26] text-black font-bold' : 'text-zinc-300 hover:bg-white/5'"
              class="w-full text-left px-4 py-2 rounded-lg text-xs font-medium flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <span class="material-symbols-outlined text-base text-[#F27D26]">storefront</span>
                <span>Aula 4: Tipos & Posicionamento</span>
              </div>
              <span class="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-mono font-bold">ATIVA</span>
            </button>

            <button 
              type="button"
              (click)="onMobileSelect('aula5')"
              [class]="activeTab() === 'aula5' ? 'bg-[#F27D26] text-black font-bold' : 'text-zinc-300 hover:bg-white/5'"
              class="w-full text-left px-4 py-2 rounded-lg text-xs font-medium flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <span class="material-symbols-outlined text-base text-[#F27D26]">movie</span>
                <span>Aula Especial: Fome de Poder</span>
              </div>
              <span class="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-mono font-bold">ATIVA</span>
            </button>
          </div>
        </div>
      }
    </header>
  `
})
export class HeaderComponent {
  activeTab = input<string>('hero');
  selectTab = output<string>();

  mobileMenuOpen = signal<boolean>(false);
  aulasDropdownOpen = signal<boolean>(false);

  navItems = [
    { id: 'ementa', label: 'Ementa Curricular', icon: 'assignment' },
    { id: 'sprint1', label: 'Sprint 1: Perfil', icon: 'memory' },
    { id: 'sprint2', label: 'Sprint 2: Infra', icon: 'hub' },
    { id: 'sprint3', label: 'Sprint 3: Agil', icon: 'published_with_changes' },
    { id: 'sprint4', label: 'Sprint 4: Canvas', icon: 'account_tree' }
  ];

  toggleAulasDropdown() {
    this.aulasDropdownOpen.update(v => !v);
  }

  toggleMobileMenu() {
    this.mobileMenuOpen.update(v => !v);
  }

  onSelect(id: string) {
    this.selectTab.emit(id);
    this.aulasDropdownOpen.set(false);
    this.mobileMenuOpen.set(false);
  }

  onMobileSelect(id: string) {
    this.onSelect(id);
  }
}
